import './product-info.scss';

import classNames from 'classnames';
import { useContext, useEffect, useState } from 'react';
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch
} from 'react-hook-form';

import { ieLeasingFormMapper } from '@/components/App/Leasing/helpers';
import type { TIeForm } from '@/components/App/Leasing/types';
import { CustomSwitch } from '@/components/Shared/Switch/CustomSwitch';
import { CarInfoModal } from '@/components/Shared/entities';
import { Autocomplete, Button, Input, Select } from '@/components/Shared/ui';
import ChevronDownIcon from '@/media/icons/chevron-down.svg?react';
import PlusIcon from '@/media/icons/plus.svg?react';
import TrashIcon from '@/media/icons/trash.svg?react';
import { formatPrice } from '@/services/formatter';

import { CreateTabContext } from '../../../IeOrder';
import {
  getCarLabel,
  getLeasingAmount,
  getSubjectCombinationKey,
  getSubjectDuplicateError,
  getSubjectsTotal,
  parseAmount
} from './helpers/subjects';
import { useSelectsOptions } from './hooks';
import { SentToSwitch } from './ui';

const ProductInfo: React.FC = () => {
  const { isFieldsFrozen } = useContext(CreateTabContext);
  const { control, trigger, setValue, getValues, clearErrors } =
    useFormContext<TIeForm>();

  const { dealers, autoTypes, loanTerms, engineTypes, repaymentTypes } =
    useSelectsOptions();

  const {
    fields: subjectFields,
    append,
    remove
  } = useFieldArray({
    control,
    name: 'subjects',
    keyName: 'fieldId'
  });

  const [subjects = [], initialPayment = '', dealer = ''] = useWatch({
    control,
    name: ['subjects', 'initialPayment', 'dealer']
  });

  const [activeSubjectIndex, setActiveSubjectIndex] = useState<number | null>(
    null
  );

  const vehiclesTotal = getSubjectsTotal(subjects);
  const leasingAmount = getLeasingAmount(subjects, initialPayment);
  const combinationKey = subjects.map(getSubjectCombinationKey).join(';');
  const activeSubject =
    activeSubjectIndex === null ? undefined : subjects[activeSubjectIndex];

  useEffect(() => {
    if (!getValues('initialPayment')) return;
    void trigger('initialPayment');
  }, [vehiclesTotal]);

  useEffect(() => {
    const list = getValues('subjects') ?? [];
    list.forEach((subject, index) => {
      if (!subject.engineType) return;
      void trigger(`subjects.${index}.engineType`);
    });
  }, [combinationKey]);

  const handleAddSubject = () => {
    append(ieLeasingFormMapper.createIeSubject(), { shouldFocus: false });
  };

  const handleDeleteSubject = (
    event: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => {
    event.preventDefault();
    if (isFieldsFrozen) return;

    if (activeSubjectIndex === index) setActiveSubjectIndex(null);

    if (subjectFields.length <= 1) {
      setValue(`subjects.${index}`, ieLeasingFormMapper.createIeSubject(), {
        shouldDirty: true
      });
      clearErrors(`subjects.${index}`);
      return;
    }

    remove(index);
  };

  const handleOpenCarModal = (index: number) => {
    if (isFieldsFrozen) return;
    setActiveSubjectIndex(index);
  };

  return (
    <div className="ie-product-info">
      <div className="ie-product-info__header">
        <p className="ie-order-step__main-title">Информация о продукте</p>
        <SentToSwitch />
      </div>

      <div className="ie-order-step__grid">
        <Controller
          control={control}
          name="dealer"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: { value: 1, message: 'Пожалуйста, введите данные' }
          }}
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              label="Торговая точка"
              selectedValue={dealers.find((item) => item.value === field.value)}
              onChange={(value) => field.onChange(value.value)}
              options={dealers}
              error={error?.message}
              isRequired
              isFrozen
            />
          )}
        />
        <Controller
          control={control}
          name="autoType"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: { value: 1, message: 'Пожалуйста, введите данные' }
          }}
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              label="Тип авто"
              selectedValue={autoTypes.find(
                (item) => item.value === field.value
              )}
              onChange={(value) => {
                field.onChange(value.value);
              }}
              options={autoTypes}
              error={error?.message}
              isFrozen={isFieldsFrozen}
              isRequired
            />
          )}
        />
      </div>

      <div className="ie-product-info__vehicles">
        {subjectFields.map((subjectField, index) => {
          const subject = subjects[index] ?? subjectField;

          return (
            <div
              className={classNames('ie-product-info__vehicle-row', {
                'ie-product-info__vehicle-row--editable': !isFieldsFrozen
              })}
              key={subjectField.fieldId}
            >
              <div className="ie-product-info__vehicle-row-title">
                Авто №{index + 1}
              </div>

              <Controller
                control={control}
                name={`subjects.${index}.brandCode`}
                rules={{
                  validate: () => {
                    const current = getValues(`subjects.${index}`);
                    if (!current?.brandCode) return 'Выберите марку автомобиля';
                    if (!current.modelCode) return 'Выберите модель автомобиля';
                    if (!current.equipmentUuid)
                      return 'Выберите комплектацию автомобиля';
                    if (!current.year) return 'Выберите год выпуска автомобиля';
                    return true;
                  }
                }}
                render={({ field: carField, fieldState: { error } }) => (
                  <Input
                    {...carField}
                    value={getCarLabel(subject)}
                    label="Марка, модель и год выпуска"
                    isRequired
                    error={error?.message}
                    className="ie-product-info__car"
                    endContent={
                      <span className="ie-product-info__arrow">
                        <ChevronDownIcon />
                      </span>
                    }
                    onChange={() => handleOpenCarModal(index)}
                    onClick={() => handleOpenCarModal(index)}
                    isFrozen={isFieldsFrozen}
                    readOnly
                    autoComplete="off"
                  />
                )}
              />
              <Controller
                control={control}
                name={`subjects.${index}.cost`}
                rules={{
                  required: 'Пожалуйста, введите данные',
                  validate: (value) => {
                    if (!value) return 'Пожалуйста, введите данные';
                    if (parseAmount(value) < 100_000)
                      return 'Стоимость авто должна быть больше 100 000 ₸';
                    return true;
                  }
                }}
                render={({ field: costField, fieldState: { error } }) => (
                  <Input
                    {...costField}
                    inputMode="numeric"
                    value={costField.value ?? ''}
                    label="Стоимость авто"
                    onChange={(event) => {
                      const value = event.target.value.replace(/\s/g, '');
                      if (value.match(/\D/) || value.length > 8) return;
                      costField.onChange(formatPrice(value));
                    }}
                    error={error?.message}
                    isFrozen={isFieldsFrozen}
                    isRequired
                  />
                )}
              />
              <Controller
                control={control}
                name={`subjects.${index}.engineType`}
                rules={{
                  required: 'Пожалуйста, введите данные',
                  validate: (value) => {
                    if (!value) return 'Пожалуйста, введите данные';
                    return getSubjectDuplicateError(
                      getValues('subjects') ?? [],
                      index
                    );
                  }
                }}
                render={({ field: engineField, fieldState: { error } }) => (
                  <Select
                    {...engineField}
                    label="Выберите двигатель"
                    selectedValue={engineTypes.find(
                      (item) => item.value === engineField.value
                    )}
                    onChange={(value) => engineField.onChange(value.value)}
                    options={engineTypes}
                    error={error?.message}
                    isFrozen={isFieldsFrozen}
                    isRequired
                  />
                )}
              />
              <div
                className={classNames('ie-product-info__last-input-container', {
                  'ie-product-info__last-input-container--editable':
                    !isFieldsFrozen
                })}
              >
                <Controller
                  control={control}
                  name={`subjects.${index}.count`}
                  rules={{
                    required: 'Пожалуйста, введите данные',
                    validate: (value) => {
                      if (!value) return 'Пожалуйста, введите данные';
                      if (Number(value) < 1)
                        return 'Количество не может быть меньше 1';
                      return true;
                    }
                  }}
                  render={({ field: countField, fieldState: { error } }) => (
                    <Input
                      {...countField}
                      inputMode="numeric"
                      value={countField.value ?? ''}
                      label="Количество"
                      onChange={(event) => {
                        const value = event.target.value.replace(/\D/g, '');
                        if (value.length > 4) return;
                        countField.onChange(value.replace(/^0+(?=\d)/, ''));
                      }}
                      error={error?.message}
                      isFrozen={isFieldsFrozen}
                      isRequired
                    />
                  )}
                />
                {!isFieldsFrozen && (
                  <Button
                    type="button"
                    aria-label="Удалить авто"
                    onClick={(event) => handleDeleteSubject(event, index)}
                    variant="outlined-red"
                    size="small"
                    className="ie-product-info__delete"
                  >
                    <TrashIcon />
                  </Button>
                )}
              </div>
            </div>
          );
        })}

        {!isFieldsFrozen && (
          <Button
            type="button"
            variant="outlined"
            fullWidth
            className="ie-product-info__add"
            onClick={handleAddSubject}
          >
            <>
              Добавить авто
              <PlusIcon />
            </>
          </Button>
        )}
      </div>

      <div className="ie-product-info__params">
        <Input
          label="Сумма лизинга"
          value={vehiclesTotal > 0 ? formatPrice(leasingAmount) : ''}
          onChange={() => undefined}
          isFrozen
          isRequired
        />
        <Controller
          control={control}
          name="initialPayment"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: { value: 1, message: 'Пожалуйста, введите данные' },
            validate: (value) => {
              const total = getSubjectsTotal(getValues('subjects'));
              const tenPercent = total * 0.1;
              const ninetyPercent = total * 0.9;
              if (parseAmount(value) < tenPercent)
                return `Значение ПВ не должно быть меньше 10% от стоимости авто (${formatPrice(tenPercent) || 0} ₸)`;
              if (total >= 100_000 && parseAmount(value) > ninetyPercent)
                return `Значение ПВ не должно быть более 90% от стоимости авто (${formatPrice(ninetyPercent) || 0} ₸)`;
              return true;
            }
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              inputMode="numeric"
              value={field.value}
              label="Первоначальный взнос"
              onChange={(event) => {
                const value = event.target.value.replace(/\s/g, '');
                if (value.match(/\D/)) return;
                field.onChange(formatPrice(value));
              }}
              error={error?.message}
              isFrozen={isFieldsFrozen}
              isRequired
            />
          )}
        />
        <Controller
          control={control}
          name="loanTerm"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: { value: 1, message: 'Пожалуйста, введите данные' },
            validate: (value) => {
              if (+value < 6 || +value > 84)
                return 'Введите значение от 6 до 84';
              return true;
            }
          }}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...field}
              inputMode="numeric"
              label="Срок лизинга"
              value={field.value ?? ''}
              onChange={(value) => {
                field.onChange(value.replace(/\D/g, ''));
              }}
              options={loanTerms}
              error={error?.message}
              isFrozen={isFieldsFrozen}
              isRequired
            />
          )}
        />
        <Controller
          control={control}
          name="paymentScheme"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: { value: 1, message: 'Пожалуйста, введите данные' }
          }}
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              label="Тип графика погашений"
              selectedValue={repaymentTypes.find(
                (item) => item.value === field.value
              )}
              onChange={(value) => field.onChange(value.value)}
              options={repaymentTypes}
              error={error?.message}
              isFrozen={isFieldsFrozen}
              isRequired
            />
          )}
        />
        <Controller
          control={control}
          name="proofInCome"
          render={({ field }) => (
            <div className="ie-product-info__switch">
              <CustomSwitch
                value={field.value}
                label="С подтверждением дохода"
                type="firstStep"
                labelTitle="С подтверждением дохода"
                onchangeCheckedHandler={field.onChange}
                fieldsReadOnly={isFieldsFrozen}
              />
            </div>
          )}
        />
      </div>

      <CarInfoModal
        key={activeSubjectIndex ?? 'closed'}
        type="autoCredit"
        isOpened={activeSubjectIndex !== null}
        onClose={() => setActiveSubjectIndex(null)}
        keyToUse="code"
        onSave={(values) => {
          if (activeSubjectIndex === null) return;

          const current = getValues(`subjects.${activeSubjectIndex}`);
          setValue(`subjects.${activeSubjectIndex}`, {
            ...current,
            brandCode: values.mark.code ?? '',
            brandName: values.mark.name,
            modelCode: values.model.code ?? '',
            modelName: values.model.name,
            equipmentUuid: values.equipment.uuid ?? '',
            equipmentName: values.equipment.name,
            year: values.year
          });
          if (values.dealer?.code) setValue('dealer', values.dealer.code);
          void trigger(`subjects.${activeSubjectIndex}.brandCode`);
          setActiveSubjectIndex(null);
        }}
        defaultValues={
          activeSubject
            ? {
                mark: activeSubject.brandName || '',
                model: activeSubject.modelName || '',
                year: activeSubject.year || '',
                equipment: activeSubject.equipmentName || '',
                dealer: dealers.find((item) => item.value === dealer)?.label
              }
            : undefined
        }
      />
    </div>
  );
};

export default ProductInfo;
